// frontend/src/features/bapb/sections/CostAssessorSection.jsx
//
// CAD Cost Approach (Beta) — v2.
//
// Math framework:
//   Base RCN (per segment) = segment.sqft × CAD published rate ($/SF)  ← from CAD's own schedule
//   Implied Net Multiplier = CAD group value ÷ Base RCN                 ← CAD's market × depreciation
//   Argued Net Multiplier   = user-editable (default: implied)
//   Argued RCNLD           = Base RCN × Argued Multiplier
//
// The argument: "CAD's own 2026 schedule sets RCN at $X. CAD's noticed value
// implies a net multiplier of Y (combined market factor + depreciation).
// We argue Z is appropriate given effective age / condition, yielding RCNLD of $W."
//
// Groups without a schedule rate (rare) fall back to a simple $ override.
//
// Data source: GET /cad/cost-detail/{cad_key}/{account}?year=YYYY

import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../apiClient";

// ── formatters ─────────────────────────────────────────────────────
const $0 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${Math.round(n).toLocaleString()}`;
};
const $2 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const pct1 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(1)}%`;
};
const num3 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(3);
};
const toNum = (v, d = 0) => {
  if (v === "" || v == null) return d;
  const n = Number(String(v).replace(/[$,%\s,]/g, ""));
  return Number.isFinite(n) ? n : d;
};

const normalizeCadKey = (s) => {
  const x = String(s || "").toLowerCase();
  const pairs = [
    ["collin", "collin"], ["brazoria", "brazoria"], ["comal", "comal"],
    ["jefferson", "jefferson"], ["kaufman", "kaufman"], ["wichita", "wichita"],
    ["lamar", "lamar"], ["wise", "wise"], ["wood", "wood"],
  ];
  for (const [needle, key] of pairs) if (x.includes(needle)) return key;
  return "";
};

// CADs with a seeded cost schedule. Only these produce a usable argument —
// others can scrape segments but have no $/SF table to back-solve against,
// which would leave the RCN + Implied × columns blank.
// Adding a CAD to this set is a separate data-seed task (see
// backend/data/cad_cost_schedules/).
const CADS_WITH_SCHEDULE = new Set(["collin"]);

// Extract the economic life in years from a Collin-style depreciation code.
//   COM30 → 30    COM40 → 40    15-COMM-SL → 15    RES65 → 65
//   MAD / unknown → null (no straight-line life available)
const lifeFromDepCode = (code) => {
  const s = String(code || "").toUpperCase();
  const m = s.match(/(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 && n < 200 ? n : null;
};

// For a group, pick the dep_code from the first segment that has a schedule_rate.
const groupDepCode = (group) => {
  for (const s of group?.segments || []) {
    const c = s?.schedule_rate?.dep_code;
    if (c) return c;
  }
  return null;
};

// Straight-line remaining factor. Returns null when inputs are missing.
const straightLineMultiplier = (effAge, life) => {
  if (!Number.isFinite(effAge) || effAge < 0) return null;
  if (!Number.isFinite(life) || life <= 0) return null;
  return Math.max(0, (life - effAge) / life);
};

// Inverse of straight-line: given a multiplier and life, what effective age
// does the CAD's value imply on their own economic-life curve?
//   multiplier = (life - age) / life   →   age = life * (1 - multiplier)
// Returns negative values when the CAD's multiplier > 1.0 — those are the
// "newer than new" cases that refute the CAD's own methodology.
const impliedEffectiveAge = (multiplier, life) => {
  if (!Number.isFinite(multiplier)) return null;
  if (!Number.isFinite(life) || life <= 0) return null;
  return life * (1 - multiplier);
};

// Compute the auto-seeded argued multiplier for a group.
//   effAge = current year - (coverYearBuilt ?? min segment year_built)
//   life   = parsed from group's dep code
//   mult   = straight-line (life - effAge) / life
const autoArguedForGroup = (group, coverYearBuilt, pullYear) => {
  const segs = group?.segments || [];
  const segYears = segs
    .map((s) => Number(s.year_built))
    .filter((y) => Number.isFinite(y) && y > 1800);
  const fallbackYB = segYears.length ? Math.min(...segYears) : null;
  const yb = Number(coverYearBuilt) || fallbackYB;
  if (!yb) return null;
  const yr = Number(pullYear) || new Date().getFullYear();
  const effAge = yr - yb;
  const life = lifeFromDepCode(groupDepCode(group));
  return straightLineMultiplier(effAge, life);
};

// ── main ───────────────────────────────────────────────────────────
export default function CostAssessorSection({ data, onChange, packet, dataKey = "cost_assessor" }) {
  // Cover defaults
  const coverCtx = useMemo(() => {
    let latest = null, maxOrder = -Infinity;
    for (const s of packet?.sections || []) {
      if (String(s?.type || "").toLowerCase() === "cover") {
        const oi = s?.order_index ?? 0;
        if (oi > maxOrder) { maxOrder = oi; latest = s; }
      }
    }
    const pi = latest?.data?.property_info || {};
    return {
      account: pi.account || pi.parcel || packet?.account_number || "",
      assessor: pi.assessor || "",
      tax_year: Number(pi.tax_year || packet?.tax_year || new Date().getFullYear()),
      year_built: Number(pi.year_built) || null,
    };
  }, [packet]);

  const initial = (data && typeof data === "object" ? data[dataKey] : undefined) || {};

  const [cadKey, setCadKey] = useState(initial.cad_key || normalizeCadKey(coverCtx.assessor));
  const [account, setAccount] = useState(initial.account || coverCtx.account);
  const [year, setYear] = useState(Number(initial.year || coverCtx.tax_year));

  const [detail, setDetail] = useState(initial.detail || null);
  const [groups, setGroups] = useState(initial.groups || []); // editable state: _argued_mult + _override per group
  const [land, setLand] = useState(initial.land || []);
  const [notes, setNotes] = useState(initial.notes || "");

  const [supported, setSupported] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/cad/cost-detail/supported")
      .then(r => { if (!cancelled) setSupported(r?.data?.supported || []); })
      .catch(() => { if (!cancelled) setSupported([]); });
    return () => { cancelled = true; };
  }, []);

  // ── Upstream sync guard ───────────────────────────────────────
  const lastUpstreamJSON = useRef("");
  const payload = useMemo(() => ({
    cad_key: cadKey, account, year, detail, groups, land, notes,
  }), [cadKey, account, year, detail, groups, land, notes]);

  useEffect(() => {
    const json = JSON.stringify(payload);
    if (json === lastUpstreamJSON.current) return;
    lastUpstreamJSON.current = json;
    onChange?.({ ...(data || {}), [dataKey]: payload });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useEffect(() => {
    const inc = data && typeof data === "object" ? data[dataKey] : undefined;
    if (!inc) return;
    const pjson = JSON.stringify(inc);
    if (pjson === lastUpstreamJSON.current) return;
    lastUpstreamJSON.current = pjson;
    if (inc.cad_key !== undefined) setCadKey(inc.cad_key);
    if (inc.account !== undefined) setAccount(inc.account);
    if (inc.year !== undefined) setYear(inc.year);
    if (inc.detail !== undefined) setDetail(inc.detail);
    if (inc.groups !== undefined) setGroups(inc.groups);
    if (inc.land !== undefined) setLand(inc.land);
    if (inc.notes !== undefined) setNotes(inc.notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify((data && data[dataKey]) || {})]);

  // ── Pull from CAD ─────────────────────────────────────────────
  const pullFromCad = async () => {
    setErr("");
    if (!cadKey) { setErr("Pick a CAD first."); return; }
    if (!account) { setErr("Account number is empty."); return; }
    if (!year) { setErr("Tax year is empty."); return; }
    setLoading(true);
    try {
      const res = await api.get(
        `/cad/cost-detail/${encodeURIComponent(cadKey)}/${encodeURIComponent(account)}`,
        { params: { year } }
      );
      const d = res?.data;
      if (!d || !d.ok) { setErr(d?.error || "Fetch failed"); setLoading(false); return; }
      setDetail(d);
      setGroups((d.groups || []).map((g, i) => {
        // Seed the argued multiplier using straight-line depreciation from the
        // Cover year-built and the dep code's economic life. This is the
        // aggressive default the board will actually be asked to accept; user
        // can adjust up/down.
        const autoMult = autoArguedForGroup(g, coverCtx.year_built, year);
        return {
          _key: `g-${i}`,
          src: g,
          argued_mult: autoMult != null
            ? String(Number(autoMult.toFixed(4)))
            : (g.implied_net_multiplier != null ? String(g.implied_net_multiplier) : ""),
          override: "",
        };
      }));
      setLand((d.land || []).map((l, i) => ({
        _key: `l-${i}`, src: l, override_per_sf: "", override_value: "",
      })));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") setErr(detail.message || "Fetch failed");
      else setErr(e?.response?.data?.detail || e?.message || "Fetch failed");
    } finally { setLoading(false); }
  };

  // ── Derived ───────────────────────────────────────────────────
  const groupRows = useMemo(() => groups.map((g) => {
    const src = g.src || {};
    const cadValue = Number(src.group_value) || 0;
    const baseRcn = src.base_rcn != null ? Number(src.base_rcn) : null;
    const implied = src.implied_net_multiplier != null ? Number(src.implied_net_multiplier) : null;
    const hasRate = !!src.has_schedule_rates;
    const argued = toNum(g.argued_mult, NaN);
    const override = toNum(g.override, NaN);

    let net;
    if (Number.isFinite(override) && override > 0) {
      net = override;
    } else if (hasRate && baseRcn != null && Number.isFinite(argued)) {
      net = Math.max(0, baseRcn * argued);
    } else {
      net = cadValue;
    }

    const totalSF = (src.segments || []).reduce((s, x) => s + (Number(x.sqft) || 0), 0);

    // Age / life info for display + narrative
    const depCode = groupDepCode(src);
    const life = lifeFromDepCode(depCode);
    const segYears = (src.segments || []).map(x => Number(x.year_built)).filter(y => Number.isFinite(y) && y > 1800);
    const segYB = segYears.length ? Math.min(...segYears) : null;
    const yb = Number(coverCtx.year_built) || segYB;
    const effAge = yb ? (Number(year) || new Date().getFullYear()) - yb : null;
    const autoMult = autoArguedForGroup(src, coverCtx.year_built, year);
    const cadImpliedAge = impliedEffectiveAge(implied, life);

    return {
      ...g,
      _cad: cadValue, _rcn: baseRcn, _implied: implied,
      _argued: Number.isFinite(argued) ? argued : null,
      _override: Number.isFinite(override) && override > 0 ? override : null,
      _net: net,
      _totalSF: totalSF,
      _hasRate: hasRate,
      _depCode: depCode,
      _life: life,
      _effAge: effAge,
      _ybUsed: yb,
      _autoMult: autoMult,
      _cadImpliedAge: cadImpliedAge,
    };
  }), [groups, coverCtx.year_built, year]);

  const landRows = useMemo(() => land.map((l) => {
    const src = l.src || {};
    const ac = Number(src.acreage) || 0;
    const sf = Number(src.sqft) || 0;
    const mv = Number(src.market_value) || 0;
    const cadPerSF = sf > 0 ? mv / sf : 0;
    const ovPerSF = toNum(l.override_per_sf, NaN);
    const ovValue = toNum(l.override_value, NaN);
    let net;
    if (Number.isFinite(ovValue) && ovValue > 0) net = ovValue;
    else if (Number.isFinite(ovPerSF) && ovPerSF > 0 && sf > 0) net = ovPerSF * sf;
    else net = mv;
    return { ...l, _ac: ac, _sf: sf, _mv: mv, _cadPerSF: cadPerSF, _net: net };
  }), [land]);

  const totals = useMemo(() => {
    const cadImprv = (detail?.totals?.improvement) ?? groupRows.reduce((s, g) => s + g._cad, 0);
    const cadLand = (detail?.totals?.land) ?? landRows.reduce((s, l) => s + l._mv, 0);
    const cadMarket = (detail?.totals?.market) ?? (cadImprv + cadLand);
    const netImprv = groupRows.reduce((s, g) => s + g._net, 0);
    const netLand = landRows.reduce((s, l) => s + l._net, 0);
    const indicated = netImprv + netLand;
    const reduction = Math.max(0, cadMarket - indicated);

    // Portfolio-level implied multiplier (informational)
    const baseRcnSum = groupRows.reduce((s, g) => s + (g._rcn || 0), 0);
    const cadImprvRated = groupRows.reduce((s, g) => s + (g._rcn != null ? g._cad : 0), 0);
    const portfolioImplied = baseRcnSum > 0 ? cadImprvRated / baseRcnSum : null;

    return { cadImprv, cadLand, cadMarket, netImprv, netLand, indicated, reduction, baseRcnSum, portfolioImplied };
  }, [detail, groupRows, landRows]);

  // ── Narrative ────────────────────────────────────────────────
  const trace = useMemo(() => {
    if (!detail) return "";
    const parts = [];
    const cadUp = String(detail.cad_key || "CAD").toUpperCase();
    parts.push(`CAD Cost Approach — ${cadUp} parcel ${account} / TY ${year}.`);
    if (detail.schedule_available) {
      parts.push(`Rates per ${cadUp} ${year} Improvement Cost Schedule (publicly posted).`);
    }
    parts.push(`CAD-noticed market value: ${$0(totals.cadMarket)} (improvements ${$0(totals.cadImprv)}, land ${$0(totals.cadLand)}).`);

    for (const g of groupRows) {
      if (g._hasRate && g._rcn != null) {
        const ageBit = (g._effAge != null && g._life)
          ? ` Actual effective age ${g._effAge} yrs on ${g._life}-yr life (CAD dep code ${g._depCode}) → straight-line factor ${num3(g._autoMult)}.`
          : "";
        // Back-solve: what effective age does CAD's multiplier imply on their own curve?
        let impliedBit = "";
        if (g._cadImpliedAge != null && g._life) {
          impliedBit = g._cadImpliedAge < 0
            ? ` That multiplier implies an effective age of ${g._cadImpliedAge.toFixed(1)} years on CAD's own ${g._depCode} curve — i.e. CAD is valuing this component as newer than Replacement Cost New, which is mathematically inconsistent with any form of depreciation.`
            : ` That multiplier implies an effective age of only ${g._cadImpliedAge.toFixed(1)} years on CAD's own ${g._depCode} curve${g._effAge != null ? ` — vs. the actual ${g._effAge} years` : ""}.`;
        }
        parts.push(
          `${g.src.name}: ${g._totalSF.toLocaleString()} SF. Schedule RCN ${$0(g._rcn)}. ` +
          `CAD's noticed value ${$0(g._cad)} implies net multiplier ${num3(g._implied)}.${impliedBit}${ageBit} ` +
          `Argued multiplier ${num3(g._argued)} → RCNLD ${$0(g._net)}.`
        );
      } else {
        parts.push(
          `${g.src.name}: CAD value ${$0(g._cad)} accepted (no schedule rate available).`
        );
      }
    }
    for (const l of landRows) {
      const desc = `${l.src.type || ""} ${l.src.description || ""}`.trim();
      parts.push(
        `Land (${desc}): ${l._sf.toLocaleString()} SF at CAD ${$2(l._cadPerSF)}/SF → ${$0(l._mv)}; indicated ${$0(l._net)}.`
      );
    }
    parts.push(
      `Indicated value: ${$0(totals.indicated)} — reduction ${$0(totals.reduction)}` +
      (totals.cadMarket > 0 ? ` (${pct1(totals.reduction / totals.cadMarket * 100)}).` : ".")
    );

    // Standards-based closing argument. The implied > 1.0 situation is the one
    // that most clearly violates the cost approach's fundamental structure.
    const anyImpliedOverOne = groupRows.some(g => g._hasRate && g._implied != null && g._implied > 1.0);
    const anyImpliedHigh = groupRows.some(g => g._hasRate && g._implied != null && g._implied > 0.9);
    if (anyImpliedOverOne || anyImpliedHigh) {
      parts.push(
        "Standards basis: USPAP Standards Rule 1 and The Appraisal of Real Estate (14th ed., Appraisal Institute) " +
        "require the cost approach to deduct physical deterioration, functional obsolescence, and external obsolescence " +
        "from Replacement Cost New. Tex. Tax Code §23.0101 requires generally accepted appraisal methodology. " +
        (anyImpliedOverOne
          ? "CAD's noticed value here exceeds Replacement Cost New per CAD's own published schedule — mathematically inconsistent with any form of depreciation and therefore with the cost approach itself. "
          : "CAD's implied net multiplier leaves essentially no room for depreciation on a building of this effective age — inconsistent with CAD's own economic-life curve. ") +
        "Applying straight-line depreciation to CAD's published RCN using CAD's own dep-code life yields the indicated value above."
      );
    }

    if (notes.trim()) parts.push(`Notes: ${notes.trim()}`);
    return parts.join(" ");
  }, [detail, groupRows, landRows, totals, account, year, notes]);

  const copyTrace = () => { navigator.clipboard?.writeText(trace).catch(() => {}); };

  const updateGroup = (idx, patch) => setGroups(prev => prev.map((g, i) => i === idx ? { ...g, ...patch } : g));
  const updateLand  = (idx, patch) => setLand(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const resetArgued = (idx) => {
    const g = groups[idx];
    if (!g) return;
    const auto = autoArguedForGroup(g.src, coverCtx.year_built, year);
    const fallback = g.src?.implied_net_multiplier;
    const seed = auto != null ? Number(auto.toFixed(4))
               : fallback != null ? Number(fallback) : "";
    updateGroup(idx, { argued_mult: seed === "" ? "" : String(seed), override: "" });
  };

  // Dropdown ordering: CADs-with-schedule first, then others (disabled).
  const orderedCads = useMemo(() => {
    const withSchedule = supported.filter(k => CADS_WITH_SCHEDULE.has(k));
    const withoutSchedule = supported.filter(k => !CADS_WITH_SCHEDULE.has(k));
    // If Collin isn't in `supported` yet (e.g. list hasn't loaded), still allow it.
    if (!withSchedule.length) withSchedule.push("collin");
    return { withSchedule, withoutSchedule };
  }, [supported]);

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <ContextStrip cad={cadKey} account={account} year={year} totals={detail ? totals : null} scheduleOk={detail?.schedule_available} />

      {/* Beta coverage notice */}
      <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm">
        <div className="font-semibold mb-0.5">Beta — Collin CAD only</div>
        <div className="text-amber-800">
          This tool rebuilds the assessor&apos;s cost approach from the CAD&apos;s own published
          cost schedule. Only <strong>Collin CAD</strong> publishes a per-class <code>$/SF</code> schedule
          we can use today — other CADs need either a published schedule or a PIA request before they
          produce a defensible argument. If you select an unsupported CAD the section will populate
          segment data but leave the RCN + multiplier columns blank.
        </div>
      </div>

      {/* Pull controls */}
      <div>
        <div className="mb-1 text-sm font-medium">Pull from Assessor</div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex flex-wrap gap-3 items-end">
          <PickerField label="CAD">
            <select
              value={cadKey} onChange={(e) => setCadKey(e.target.value)}
              className="rounded-lg border bg-white border-gray-300 px-3 py-2 text-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none"
            >
              <option value="">Select…</option>
              {orderedCads.withSchedule.length > 0 && (
                <optgroup label="Supported (cost schedule seeded)">
                  {orderedCads.withSchedule.map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </optgroup>
              )}
              {orderedCads.withoutSchedule.length > 0 && (
                <optgroup label="Schedule pending — segments only">
                  {orderedCads.withoutSchedule.map(k => (
                    <option key={k} value={k} disabled>
                      {k} (schedule pending)
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </PickerField>
          <PickerField label="Account">
            <input
              value={account} onChange={(e) => setAccount(e.target.value)}
              className="w-44 rounded-lg border bg-white border-gray-300 px-3 py-2 text-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none"
            />
          </PickerField>
          <PickerField label="Year">
            <input
              type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="w-24 rounded-lg border bg-white border-gray-300 px-3 py-2 text-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none"
            />
          </PickerField>
          <button
            type="button" onClick={pullFromCad} disabled={loading}
            className={[
              "rounded-lg px-4 py-2 text-sm transition",
              loading ? "bg-slate-300 text-slate-600 cursor-not-allowed" : "bg-slate-900 text-white hover:bg-slate-800",
            ].join(" ")}
          >
            {loading ? "Pulling…" : "Pull from CAD"}
          </button>
          {detail?.url && (
            <a href={detail.url} target="_blank" rel="noreferrer" className="text-xs text-slate-600 hover:text-slate-900 underline">
              open CAD page ↗
            </a>
          )}
          {err && <div className="text-sm text-rose-700 w-full">{err}</div>}
        </div>
      </div>

      {detail && !detail.schedule_available && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm">
          No published cost schedule seeded for <strong>{cadKey.toUpperCase()} {year}</strong>. RCN and implied multiplier columns will be blank; groups fall back to a plain $ override.
        </div>
      )}

      {/* Improvement groups */}
      {groupRows.length > 0 && (
        <div>
          <div className="mb-1 text-sm font-medium">Improvement Groups</div>
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Group</th>
                    <th className="px-3 py-2 text-right font-medium">CAD Value</th>
                    <th className="px-3 py-2 text-right font-medium">SF</th>
                    <th className="px-3 py-2 text-right font-medium">Base RCN</th>
                    <th className="px-3 py-2 text-right font-medium" title="CAD value ÷ Base RCN">Implied ×</th>
                    <th className="px-3 py-2 text-right font-medium" title="Your proposed multiplier">Argued ×</th>
                    <th className="px-3 py-2 text-right font-medium">Override $</th>
                    <th className="px-3 py-2 text-right font-medium">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map((g, i) => (
                    <React.Fragment key={g._key || i}>
                      <tr className="border-t border-slate-200">
                        <td className="px-3 py-2 align-top">
                          <div className="font-medium">{g.src.name}</div>
                          <div className="text-xs text-slate-500">
                            {g.src.category}{g.src.type ? ` · ${g.src.type}` : ""}
                          </div>
                          {g._hasRate && (g._depCode || g._effAge != null) && (
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {g._depCode ? `${g._depCode}` : ""}
                              {g._life ? ` · ${g._life}-yr life` : ""}
                              {g._effAge != null ? ` · eff age ${g._effAge}` : ""}
                              {g._ybUsed ? ` (${Number(coverCtx.year_built) ? "cover" : "CAD"} YB ${g._ybUsed})` : ""}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{$0(g._cad)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{g._totalSF.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {g._rcn != null ? $0(g._rcn) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {g._implied != null ? (
                            <div className="flex flex-col items-end">
                              <span className="text-slate-600">{num3(g._implied)}</span>
                              {g._cadImpliedAge != null && g._life && (
                                <span
                                  className={[
                                    "text-[11px]",
                                    g._cadImpliedAge < 0
                                      ? "text-rose-600 font-medium"
                                      : (g._effAge != null && g._cadImpliedAge < g._effAge)
                                        ? "text-amber-700"
                                        : "text-slate-500",
                                  ].join(" ")}
                                  title={g._cadImpliedAge < 0
                                    ? "CAD's multiplier implies the building is newer than new — i.e. appreciation, not depreciation"
                                    : "CAD's multiplier implies this effective age on their own curve"}
                                >
                                  ≈ age {g._cadImpliedAge.toFixed(1)}
                                  {g._cadImpliedAge < 0 ? " (newer than new)" : ""}
                                </span>
                              )}
                            </div>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {g._hasRate ? (
                            <div className="flex flex-col items-end gap-0.5">
                              <div className="inline-flex items-center gap-1">
                                <SmallNumberInput
                                  value={g.argued_mult}
                                  step="0.001"
                                  onChange={v => updateGroup(i, { argued_mult: v })}
                                  width="w-20"
                                />
                                <button
                                  type="button" onClick={() => resetArgued(i)}
                                  className="text-xs text-slate-500 hover:text-slate-900 underline"
                                  title={g._autoMult != null
                                    ? `Reset to straight-line ${num3(g._autoMult)} (eff age ${g._effAge} / ${g._life}-yr life)`
                                    : "Reset to CAD implied multiplier"}
                                >↺</button>
                              </div>
                              <div className="text-[11px] text-slate-400">
                                {g._autoMult != null ? `SL: ${num3(g._autoMult)}` : ""}
                              </div>
                            </div>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <SmallTextInput value={g.override} onChange={v => updateGroup(i, { override: v })} placeholder="—" />
                        </td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{$0(g._net)}</td>
                      </tr>
                      <tr>
                        <td colSpan={8} className="px-3 pt-0 pb-2 bg-slate-50/60">
                          <div className="flex flex-wrap gap-1.5">
                            {(g.src.segments || []).map((s, si) => {
                              const sr = s.schedule_rate;
                              return (
                                <Badge key={si}>
                                  <strong>{s.class_cd || s.type}</strong>
                                  {s.description ? ` — ${s.description}` : ""}
                                  {s.sqft ? ` · ${Number(s.sqft).toLocaleString()} SF` : ""}
                                  {sr ? ` · ${$2(sr.price_per_sf)}/SF [${sr.dep_code}]` : ""}
                                  {s.segment_rcn ? ` · RCN ${$0(s.segment_rcn)}` : ""}
                                  {s.year_built ? ` · ${s.year_built}` : ""}
                                  {sr?.matched_fallback ? <em className="ml-1 text-amber-700"> (detail fallback)</em> : null}
                                </Badge>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 text-slate-700">
                  <tr className="border-t border-slate-200">
                    <td className="px-3 py-2 text-right font-medium" colSpan={3}>Improvements subtotal</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{totals.baseRcnSum > 0 ? $0(totals.baseRcnSum) : "—"}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-600">
                      {totals.portfolioImplied != null ? num3(totals.portfolioImplied) : "—"}
                    </td>
                    <td colSpan={2} />
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{$0(totals.netImprv)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Land */}
      {landRows.length > 0 && (
        <div>
          <div className="mb-1 text-sm font-medium">Land</div>
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Type / Description</th>
                    <th className="px-3 py-2 text-right font-medium">Acres</th>
                    <th className="px-3 py-2 text-right font-medium">SF</th>
                    <th className="px-3 py-2 text-right font-medium">CAD Value</th>
                    <th className="px-3 py-2 text-right font-medium">CAD $/SF</th>
                    <th className="px-3 py-2 text-right font-medium">Override $/SF</th>
                    <th className="px-3 py-2 text-right font-medium">Override $</th>
                    <th className="px-3 py-2 text-right font-medium">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {landRows.map((l, i) => (
                    <tr key={l._key || i} className="border-t border-slate-200">
                      <td className="px-3 py-2">
                        <div className="font-medium">{l.src.type}</div>
                        <div className="text-xs text-slate-500">{l.src.description}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{l._ac ? l._ac.toFixed(2) : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{l._sf ? l._sf.toLocaleString() : "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{$0(l._mv)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{$2(l._cadPerSF)}</td>
                      <td className="px-3 py-2 text-right">
                        <SmallTextInput value={l.override_per_sf} onChange={v => updateLand(i, { override_per_sf: v })} placeholder="—" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <SmallTextInput value={l.override_value} onChange={v => updateLand(i, { override_value: v })} placeholder="—" />
                      </td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">{$0(l._net)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 text-slate-700">
                  <tr className="border-t border-slate-200">
                    <td className="px-3 py-2 text-right font-medium" colSpan={7}>Land subtotal</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">{$0(totals.netLand)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div>
          <div className="mb-1 text-sm font-medium">Indicated Value</div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <ValueCell label="CAD Noticed Market" value={$0(totals.cadMarket)} />
              <ValueCell label="Cost Indicated" value={$0(totals.indicated)} emphasis />
              <ValueCell
                label="Proposed Reduction"
                value={`${$0(totals.reduction)}${totals.cadMarket > 0 ? ` (${pct1(totals.reduction / totals.cadMarket * 100)})` : ""}`}
                emphasis
              />
            </div>
          </div>
        </div>
      )}

      <Notes value={notes} onChange={setNotes} />

      {detail && (
        <div>
          <div className="mb-1 flex items-center justify-between">
            <div className="text-sm font-medium">Packet Narrative</div>
            <button
              type="button" onClick={copyTrace}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs hover:bg-slate-50"
            >Copy</button>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 whitespace-pre-wrap">
            {trace}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- primitives ---------------- */

function ContextStrip({ cad, account, year, totals, scheduleOk }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm flex flex-wrap items-center gap-3">
      <span className="text-slate-600">Context:</span>
      <Badge>CAD: <strong>{cad ? cad.toUpperCase() : "—"}</strong></Badge>
      <Badge>Account: <strong>{account || "—"}</strong></Badge>
      <Badge>Year: <strong>{year || "—"}</strong></Badge>
      {scheduleOk && <Badge><strong className="text-emerald-700">Cost schedule ✓</strong></Badge>}
      {totals && (
        <>
          <span className="h-4 w-px bg-slate-300" />
          <Badge>CAD Improv: <strong>{$0(totals.cadImprv)}</strong></Badge>
          <Badge>CAD Land: <strong>{$0(totals.cadLand)}</strong></Badge>
          <Badge>CAD Market: <strong>{$0(totals.cadMarket)}</strong></Badge>
        </>
      )}
    </div>
  );
}

function PickerField({ label, children }) {
  return (<div><label className="block text-xs text-gray-500 mb-1">{label}</label>{children}</div>);
}

function SmallNumberInput({ value, onChange, placeholder = "0", width = "w-16", step }) {
  return (
    <input
      type="number" step={step || "any"}
      value={value}
      onChange={e => onChange?.(e.target.value)}
      placeholder={placeholder}
      className={`${width} rounded-md border border-gray-300 bg-white px-2 py-1 text-right text-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none`}
    />
  );
}

function SmallTextInput({ value, onChange, placeholder }) {
  return (
    <input
      value={value} onChange={e => onChange?.(e.target.value)} placeholder={placeholder}
      className="w-24 rounded-md border border-gray-300 bg-white px-2 py-1 text-right text-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none"
    />
  );
}

function ValueCell({ label, value, emphasis }) {
  return (
    <div>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className={emphasis ? "text-lg font-semibold" : "text-base"}>{value}</div>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs">
      {children}
    </span>
  );
}

function Notes({ value, onChange }) {
  return (
    <div>
      <div className="mb-1 text-sm font-medium">Notes</div>
      <textarea
        value={value} onChange={e => onChange?.(e.target.value)} rows={3}
        placeholder="Effective-age argument, condition evidence, reclassification, external obsolescence context, etc."
        className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm focus:border-slate-400 focus:ring-2 focus:ring-slate-200 outline-none"
      />
    </div>
  );
}
